import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown } from "./icons.js";

export interface DropdownOption<T extends string | number> {
  value: T;
  label: string;
  /** A short second line under the label. */
  hint?: string;
  disabled?: boolean;
}

/**
 * The one dropdown for the requirement pages (#238) — replaces native <select>s so every field
 * looks the same and the WHOLE field (label, value and arrow) opens the list, not just the text.
 * Keyboard: Enter / Space / ↓ open, ↑ ↓ move, Enter picks, Escape closes.
 *
 * `children` replaces the trigger's default content (value + arrow) for controls that carry their
 * own look, e.g. the status pill with its coloured dot; the trigger is still one button.
 */
export function Dropdown<T extends string | number>({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  placeholder = "Select…",
  className,
  triggerClassName,
  align = "left",
  children,
}: {
  value: T | null;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  align?: "left" | "right";
  children?: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const openList = (): void => {
    if (disabled) return;
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : options.findIndex((o) => !o.disabled));
    setOpen(true);
  };
  const pick = (o: DropdownOption<T>): void => {
    if (o.disabled) return;
    setOpen(false);
    if (o.value !== value) onChange(o.value);
  };
  const move = (dir: 1 | -1): void => {
    if (options.length === 0) return;
    let i = active;
    for (let n = 0; n < options.length; n++) {
      i = (i + dir + options.length) % options.length;
      if (!options[i]!.disabled) break;
    }
    setActive(i);
  };

  return (
    <div ref={rootRef} className={`dd-root${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className={triggerClassName ?? "dd-trigger"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openList();
          } else if (open && e.key === "ArrowDown") {
            e.preventDefault();
            move(1);
          } else if (open && e.key === "ArrowUp") {
            e.preventDefault();
            move(-1);
          } else if (open && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            const o = options[active];
            if (o) pick(o);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        {children ?? (
          <>
            <span className={`dd-value${selected ? "" : " empty"}`}>{selected?.label ?? placeholder}</span>
            <ChevronDown className="dd-chevron" aria-hidden="true" />
          </>
        )}
      </button>
      {open && (
        <ul id={listId} role="listbox" aria-label={ariaLabel} className={`dd-menu${align === "right" ? " right" : ""}`}>
          {options.map((o, i) => (
            <li
              key={String(o.value)}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled || undefined}
              className={`dd-opt${i === active ? " active" : ""}${o.value === value ? " selected" : ""}${o.disabled ? " disabled" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span className="dd-opt-text">
                <span>{o.label}</span>
                {o.hint && <span className="dd-opt-hint">{o.hint}</span>}
              </span>
              {o.value === value && <Check className="dd-opt-check" aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
